import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeft, HelpCircle, MessageSquare, Plus, Loader,
  ChevronDown, ChevronUp, Clock, CheckCircle, Circle,
  AlertCircle, Send, Search
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  open: { label: 'Open', color: '#FF3B30', icon: Circle },
  in_progress: { label: 'In Progress', color: '#FFD60A', icon: AlertCircle },
  resolved: { label: 'Resolved', color: '#34C759', icon: CheckCircle },
  closed: { label: 'Closed', color: '#5C5C5C', icon: CheckCircle }
};

const CATEGORIES = ['general', 'bug', 'feature', 'billing'];

const SupportPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('faq');
  const [faqs, setFaqs] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [showNewFaq, setShowNewFaq] = useState(false);
  const [creating, setCreating] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [faqSearch, setFaqSearch] = useState('');

  // Ticket form
  const [ticketForm, setTicketForm] = useState({ subject: '', description: '', category: 'general' });
  // FAQ form
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'General', order: 0 });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [faqRes, ticketRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/support/faq`).catch(() => ({ data: [] })),
        user ? axios.get(`${BACKEND_URL}/api/support/tickets`, { withCredentials: true }).catch(() => ({ data: [] })) : Promise.resolve({ data: [] })
      ]);
      setFaqs(faqRes.data);
      setTickets(ticketRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.subject || !ticketForm.description) { toast.error('Subject and description required'); return; }
    setCreating(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/support/tickets`, ticketForm, { withCredentials: true });
      toast.success(`Ticket #${data.number} created!`);
      setShowNewTicket(false);
      setTicketForm({ subject: '', description: '', category: 'general' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFaq = async () => {
    if (!faqForm.question || !faqForm.answer) { toast.error('Question and answer required'); return; }
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/support/faq`, faqForm, { withCredentials: true });
      toast.success('FAQ created!');
      setShowNewFaq(false);
      setFaqForm({ question: '', answer: '', category: 'General', order: 0 });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create FAQ');
    } finally {
      setCreating(false);
    }
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setReplying(true);
    try {
      await axios.post(`${BACKEND_URL}/api/support/tickets/${selectedTicket._id}/reply`, { message: replyText }, { withCredentials: true });
      toast.success('Reply sent!');
      setReplyText('');
      // Refresh ticket
      const { data } = await axios.get(`${BACKEND_URL}/api/support/tickets/${selectedTicket._id}`, { withCredentials: true });
      setSelectedTicket(data);
      fetchData();
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const handleStatusChange = async (ticketId, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/support/tickets/${ticketId}/status`, { status: newStatus }, { withCredentials: true });
      toast.success(`Status updated to ${newStatus}`);
      fetchData();
      if (selectedTicket?._id === ticketId) {
        const { data } = await axios.get(`${BACKEND_URL}/api/support/tickets/${ticketId}`, { withCredentials: true });
        setSelectedTicket(data);
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Delete this FAQ?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/support/faq/${faqId}`, { withCredentials: true });
      toast.success('FAQ deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const filteredFaqs = faqSearch
    ? faqs.filter(f => f.question.toLowerCase().includes(faqSearch.toLowerCase()) || f.answer.toLowerCase().includes(faqSearch.toLowerCase()))
    : faqs;

  const isAdmin = user?.role === 'admin';

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8" data-testid="support-page">
      <Toaster position="top-right" />
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Support
        </h1>
        <p className="text-[var(--text-secondary)] mb-8">Find answers or get help</p>

        {/* Tabs */}
        <div className="flex gap-2 mb-8">
          <button
            onClick={() => { setActiveTab('faq'); setSelectedTicket(null); }}
            data-testid="tab-faq"
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-2 transition-all ${
              activeTab === 'faq' ? 'bg-[var(--obsidian)] text-white border-[var(--border-default)]' : 'border-[var(--border-subtle)] text-[var(--text-secondary)]'
            }`}
          >
            <HelpCircle size={14} /> FAQ
          </button>
          <button
            onClick={() => { setActiveTab('tickets'); setSelectedTicket(null); }}
            data-testid="tab-tickets"
            className={`flex items-center gap-2 px-5 py-2.5 text-sm font-bold border-2 transition-all ${
              activeTab === 'tickets' ? 'bg-[var(--obsidian)] text-white border-[var(--border-default)]' : 'border-[var(--border-subtle)] text-[var(--text-secondary)]'
            }`}
          >
            <MessageSquare size={14} /> Tickets
            {tickets.filter(t => t.status === 'open').length > 0 && (
              <span className="bg-[var(--primary)] text-white text-[10px] px-1.5 py-0.5 font-bold">
                {tickets.filter(t => t.status === 'open').length}
              </span>
            )}
          </button>
        </div>

        {/* FAQ Tab */}
        {activeTab === 'faq' && (
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="flex-1 relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                <input
                  value={faqSearch}
                  onChange={(e) => setFaqSearch(e.target.value)}
                  placeholder="Search FAQs..."
                  className="w-full pl-10 pr-4 py-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm"
                  data-testid="faq-search"
                />
              </div>
              {isAdmin && (
                <button
                  onClick={() => setShowNewFaq(true)}
                  data-testid="add-faq-btn"
                  className="bg-[var(--obsidian)] text-white font-bold px-4 py-3 border-2 border-[var(--border-default)] flex items-center gap-2 text-sm"
                >
                  <Plus size={14} /> Add FAQ
                </button>
              )}
            </div>

            {filteredFaqs.length === 0 ? (
              <div className="border-2 border-[var(--border-default)] p-12 text-center">
                <HelpCircle size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                <p className="text-[var(--text-secondary)]">
                  {faqSearch ? 'No matching FAQs found.' : 'No FAQs yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {filteredFaqs.map((faq) => (
                  <div key={faq._id} className="bg-[var(--surface)] border-2 border-[var(--border-default)]">
                    <button
                      onClick={() => setExpandedFaq(expandedFaq === faq._id ? null : faq._id)}
                      data-testid={`faq-item-${faq._id}`}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <span className="font-bold text-sm pr-4">{faq.question}</span>
                      {expandedFaq === faq._id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                    </button>
                    {expandedFaq === faq._id && (
                      <div className="px-4 pb-4 border-t border-[var(--border-subtle)]">
                        <p className="text-sm text-[var(--text-secondary)] pt-3 leading-relaxed">{faq.answer}</p>
                        {faq.category && (
                          <span className="inline-block mt-2 px-2 py-0.5 text-[10px] border border-[var(--border-subtle)] text-[var(--text-muted)]">
                            {faq.category}
                          </span>
                        )}
                        {isAdmin && (
                          <button onClick={() => handleDeleteFaq(faq._id)} className="ml-3 text-xs text-[var(--primary)] hover:underline">
                            Delete
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && !selectedTicket && (
          <div>
            <div className="flex justify-end mb-6">
              <button
                onClick={() => setShowNewTicket(true)}
                data-testid="new-ticket-btn"
                className="bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] flex items-center gap-2"
              >
                <Plus size={16} /> New Ticket
              </button>
            </div>

            {tickets.length === 0 ? (
              <div className="border-2 border-[var(--border-default)] p-12 text-center">
                <MessageSquare size={32} className="mx-auto mb-3 text-[var(--text-muted)]" />
                <p className="text-[var(--text-secondary)]">No support tickets yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {tickets.map((ticket) => {
                  const sc = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
                  const StatusIcon = sc.icon;
                  return (
                    <button
                      key={ticket._id}
                      onClick={async () => {
                        const { data } = await axios.get(`${BACKEND_URL}/api/support/tickets/${ticket._id}`, { withCredentials: true });
                        setSelectedTicket(data);
                      }}
                      data-testid={`ticket-${ticket.number}`}
                      className="w-full bg-[var(--surface)] border-2 border-[var(--border-default)] p-4 text-left hover:bg-[var(--surface-alt)] transition-colors flex items-center gap-4"
                    >
                      <StatusIcon size={18} style={{ color: sc.color }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs text-[var(--text-muted)] font-mono">#{ticket.number}</span>
                          <span className="font-bold text-sm truncate">{ticket.subject}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-[var(--text-muted)]">
                          <span style={{ color: sc.color }}>{sc.label}</span>
                          <span>{ticket.category}</span>
                          {isAdmin && <span>{ticket.user_email}</span>}
                          <span>{ticket.replies?.length || 0} replies</span>
                        </div>
                      </div>
                      <Clock size={14} className="text-[var(--text-muted)]" />
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Ticket Detail */}
        {activeTab === 'tickets' && selectedTicket && (
          <div>
            <button onClick={() => setSelectedTicket(null)} className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6">
              <ArrowLeft size={14} /> All Tickets
            </button>

            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-6 mb-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-mono text-[var(--text-muted)]">#{selectedTicket.number}</span>
                    <span className="text-xs px-2 py-0.5 border font-bold" style={{ borderColor: STATUS_CONFIG[selectedTicket.status]?.color, color: STATUS_CONFIG[selectedTicket.status]?.color }}>
                      {STATUS_CONFIG[selectedTicket.status]?.label}
                    </span>
                  </div>
                  <h2 className="text-xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{selectedTicket.subject}</h2>
                </div>
                {isAdmin && (
                  <select
                    value={selectedTicket.status}
                    onChange={(e) => handleStatusChange(selectedTicket._id, e.target.value)}
                    className="border-2 border-[var(--border-default)] bg-[var(--surface)] px-3 py-1.5 text-sm font-bold"
                    data-testid="ticket-status-select"
                  >
                    {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                      <option key={k} value={k}>{v.label}</option>
                    ))}
                  </select>
                )}
              </div>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">{selectedTicket.description}</p>
              <div className="flex items-center gap-3 text-xs text-[var(--text-muted)] mt-3">
                <span>{selectedTicket.user_name || selectedTicket.user_email}</span>
                <span>{selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : ''}</span>
              </div>
            </div>

            {/* Replies */}
            {selectedTicket.replies?.length > 0 && (
              <div className="space-y-3 mb-6">
                {selectedTicket.replies.map((r, i) => (
                  <div key={i} className={`p-4 border-2 ${r.is_admin ? 'border-[var(--secondary)] bg-[var(--secondary)]/5' : 'border-[var(--border-default)] bg-[var(--surface)]'}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-sm font-bold">{r.author_name}</span>
                      {r.is_admin && <span className="text-[10px] bg-[var(--secondary)] text-white px-1.5 py-0.5 font-bold">STAFF</span>}
                      <span className="text-xs text-[var(--text-muted)]">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</span>
                    </div>
                    <p className="text-sm text-[var(--text-secondary)]">{r.message}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Reply Box */}
            <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-4">
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows={3}
                className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none mb-3"
                data-testid="ticket-reply-input"
              />
              <button
                onClick={handleReply}
                disabled={replying || !replyText.trim()}
                data-testid="send-reply-btn"
                className="bg-[var(--obsidian)] text-white font-bold px-6 py-2.5 border-2 border-[var(--border-default)] disabled:opacity-50 flex items-center gap-2 text-sm"
              >
                {replying ? <Loader size={14} className="animate-spin" /> : <Send size={14} />}
                Send Reply
              </button>
            </div>
          </div>
        )}

        {/* New Ticket Dialog */}
        <Dialog open={showNewTicket} onOpenChange={setShowNewTicket}>
          <DialogContent className="max-w-md bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
            <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>New Ticket</DialogTitle>
              <DialogDescription className="text-sm text-[var(--text-secondary)]">Describe your issue and we'll get back to you</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Category</label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <button key={c} onClick={() => setTicketForm({...ticketForm, category: c})}
                      className={`px-3 py-1.5 text-xs font-bold border-2 capitalize transition-colors ${ticketForm.category === c ? 'bg-[var(--obsidian)] text-white border-[var(--border-default)]' : 'border-[var(--border-subtle)]'}`}>
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Subject</label>
                <input value={ticketForm.subject} onChange={(e) => setTicketForm({...ticketForm, subject: e.target.value})}
                  placeholder="Brief summary" className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" data-testid="ticket-subject-input" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Description</label>
                <textarea value={ticketForm.description} onChange={(e) => setTicketForm({...ticketForm, description: e.target.value})}
                  placeholder="Detailed description..." rows={4} className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none" data-testid="ticket-description-input" />
              </div>
              <button onClick={handleCreateTicket} disabled={creating} data-testid="submit-ticket-btn"
                className="w-full bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50">
                {creating ? 'Creating...' : 'Submit Ticket'}
              </button>
            </div>
          </DialogContent>
        </Dialog>

        {/* New FAQ Dialog (Admin) */}
        <Dialog open={showNewFaq} onOpenChange={setShowNewFaq}>
          <DialogContent className="max-w-md bg-[var(--surface)] border border-[var(--border-default)] p-0" style={{ borderRadius: 0 }}>
            <DialogHeader className="p-6 pb-4 border-b border-[var(--border-default)]">
              <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>New FAQ</DialogTitle>
              <DialogDescription className="text-sm text-[var(--text-secondary)]">Add a frequently asked question</DialogDescription>
            </DialogHeader>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Question</label>
                <input value={faqForm.question} onChange={(e) => setFaqForm({...faqForm, question: e.target.value})}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" data-testid="faq-question-input" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Answer</label>
                <textarea value={faqForm.answer} onChange={(e) => setFaqForm({...faqForm, answer: e.target.value})}
                  rows={4} className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm resize-none" data-testid="faq-answer-input" />
              </div>
              <div>
                <label className="block text-sm font-bold mb-1">Category</label>
                <input value={faqForm.category} onChange={(e) => setFaqForm({...faqForm, category: e.target.value})}
                  className="w-full p-3 border-2 border-[var(--border-default)] bg-[var(--surface)] text-sm" />
              </div>
              <button onClick={handleCreateFaq} disabled={creating} data-testid="save-faq-btn"
                className="w-full bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] disabled:opacity-50">
                {creating ? 'Creating...' : 'Create FAQ'}
              </button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default SupportPage;
